import React from 'react';
import EventEmitter from 'events';

import util from '../tools/util';
import UserStore from './user_store';

const g_volumeListMap = new Map();

const g_eventEmitter = new EventEmitter();
const CHANGE_EVENT = 'change';
function once(callback) {
  g_eventEmitter.once(CHANGE_EVENT, callback);
}
function addChangeListener(callback) {
  g_eventEmitter.on(CHANGE_EVENT, callback);
}
function removeChangeListener(callback) {
  g_eventEmitter.removeListener(CHANGE_EVENT, callback);
}
function _emit(...args) {
  g_eventEmitter.emit(CHANGE_EVENT, ...args);
}
function init(done) {
  UserStore.addChangeListener(_onUserChange);
  done?.();
}
function _onUserChange() {
  if (!UserStore.isLoggedIn()) {
    g_volumeListMap.clear();
    _emit('update');
  }
}
function isReady() {
  return true;
}
function getVolumeList(org_id: number) {
  return g_volumeListMap.get(org_id);
}
function getVolume(org_id: number, volume_id: number) {
  return getVolumeList(org_id)?.find?.((v) => v.volume_id === volume_id);
}
function _subscribe(callback: any) {
  addChangeListener(callback);
  return () => {
    removeChangeListener(callback);
  };
}
export function useVolumeList(org_id: number) {
  const _get = React.useCallback(() => getVolumeList(org_id), [org_id]);
  return React.useSyncExternalStore(_subscribe, _get);
}
export function useVolumeAccess(org_id: number, volume_id: number) {
  const list = useVolumeList(org_id);
  const found = list?.find?.((v) => v.volume_id === volume_id);
  return list ? Boolean(found?.is_user_volume) : undefined;
}
export function useVolume(org_id: number, volume_id: number) {
  const list = useVolumeList(org_id);
  return list?.find?.((v) => v.volume_id === volume_id);
}
const fetch = util.herd(
  (org_id: number, done?: (err?: any) => void) => {
    const opts = {
      url: `/api/1/org/${org_id}/volume`,
    };
    UserStore.get(opts, (err, body) => {
      if (err) {
        util.errorLog('VolumeStore.fetch: err:', err);
      } else if (body) {
        const old = g_volumeListMap.get(org_id);
        if (!util.deepEqual(old, body.volume_list)) {
          g_volumeListMap.set(org_id, body.volume_list);
          _emit('update');
        }
      }
      done?.(err);
    });
  },
  (arg) => String(arg)
);
function createVolume(params: any, done: (err: any, volume_id?: any) => void) {
  const { org_id, volume_name } = params;
  const opts = {
    url: `/api/1/org/${org_id}/volume`,
    body: {
      org_id,
      volume_name,
    },
  };
  UserStore.post(opts, (err, body) => {
    if (err) {
      util.errorLog('VolumeStore.createVolume: err:', err);
      done(err);
    } else {
      fetch(org_id, () => done(null, body.volume_id));
    }
  });
}
function updateVolume(params: any, done: (err?: any) => void) {
  const { org_id, volume_id, ...body } = params;
  const opts = {
    url: `/api/1/org/${org_id}/volume/${volume_id}`,
    body,
  };
  UserStore.post(opts, (err) => {
    if (err) {
      util.errorLog('VolumeStore.updateVolume: err:', err);
      done(err);
    } else {
      fetch(org_id, () => done());
    }
  });
}
export default {
  once,
  addChangeListener,
  removeChangeListener,
  init,
  isReady,
  getVolumeList,
  getVolume,
  useVolumeList,
  useVolume,
  useVolumeAccess,
  fetch,
  createVolume,
  updateVolume,
};
